// Phase 38 · 23b — where tickets arrive from.
//
// Same argument as the status breakdown: a ranked list carries the count, the
// share and the ordering at once. The per-channel icons are gone from the rows
// — a tinted glyph repeated down a column is noise, and the channel name is
// unambiguous where the glyph is not.

import { BarChart, DonutChart } from '../charts';
import { ranked, leader } from '../../features/dashboard/widgetFindings';
import { WidgetShell, WidgetEmpty, RankedList, Found } from './WidgetShell';

interface ChannelData {
  name: string;
  value: number;
  color: string;
  icon: 'email' | 'chat' | 'phone' | 'whatsapp' | 'social';
}

interface ChannelDistributionWidgetProps {
  title?: string;
  data?: ChannelData[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** `ranked` is the 23b default; the chart types remain for configured widgets. */
  chartType?: 'ranked' | 'horizontal-bar' | 'bar' | 'pie';
}

export function ChannelDistributionWidget({
  data,
  loading = false,
  error,
  onRetry,
  className = '',
  chartType = 'ranked',
  title,
}: ChannelDistributionWidgetProps) {
  const total = data ? data.reduce((sum, item) => sum + item.value, 0) : 0;
  const rows = ranked(data?.map((d) => ({ label: d.name, value: d.value, color: d.color })));
  const top = leader(rows);

  const finding =
    top && top.share !== null ? (
      <>
        <Found>
          {top.label} carries {Math.round(top.share)}% of intake
        </Found>{' '}
        — {top.value} of {total} tickets arrived that way.
      </>
    ) : null;

  return (
    <WidgetShell
      title={title || 'Tickets by Channel'}
      subtitle={loading ? undefined : `${total} tickets`}
      loading={loading}
      error={error}
      onRetry={onRetry}
      className={className}
      finding={finding}
      empty={
        !data || data.length === 0 ? (
          <WidgetEmpty
            title="No channel data"
            hint="The split appears once tickets arrive through a channel."
          />
        ) : undefined
      }
      bodyClassName={chartType === 'ranked' ? 'overflow-y-auto co-scrollbar-none' : 'p-3'}
    >
      {chartType === 'ranked' ? (
        <RankedList rows={rows} />
      ) : chartType === 'pie' ? (
        <DonutChart
          data={data!.map((d) => ({ name: d.name, value: d.value, color: d.color }))}
          showLegend={false}
          showLabels
        />
      ) : (
        <BarChart
          data={data!.map((d) => ({ name: d.name, value: d.value, color: d.color }))}
          bars={[{ dataKey: 'value', name: 'Tickets' }]}
          showLegend={false}
          colorByValue
          layout={chartType === 'horizontal-bar' ? 'vertical' : 'horizontal'}
        />
      )}
    </WidgetShell>
  );
}
